import { eventMessages } from 'src/features/eventLog/utils/messages';
import { EVENT_TYPE } from 'src/features/eventLog/types';
import {
    ALIGN,
    CAMERA_TYPE,
    COMMUNICATOR_TYPE,
    FISCAL_DEVICE_TYPE,
    GATEWAY_DISPLAY_MODEL,
    IDLE_STATE,
    LISTEN_STATE,
    MEMBERSHIP_CATEGORY,
    ORIENTATION,
    POSITION,
    SYSTEM_COMPONENT,
    SYSTEM_COMPONENT_STATUS,
    SYSTEM_COMPONENT_TYPE,
    TICKET_PRINTER_ROW_TYPE,
} from 'src/features/systems/utils/types';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { BadgeOption } from 'src/registry/types/badges';

export const membershipCategoryTranslationMap: Record<MEMBERSHIP_CATEGORY, { id: string; defaultMessage: string }> = {
    [MEMBERSHIP_CATEGORY.VIP]: visitorMessages.vip,
    [MEMBERSHIP_CATEGORY.RESIDENT]: visitorMessages.resident,
    [MEMBERSHIP_CATEGORY.STAFF]: visitorMessages.staff,
    [MEMBERSHIP_CATEGORY.SERVICE]: visitorMessages.service,
    [MEMBERSHIP_CATEGORY.SUBSCRIPTION]: visitorMessages.subscription,
    [MEMBERSHIP_CATEGORY.RESERVATION]: visitorMessages.reservation,
};

export const eventTypeTranslationMap: Record<EVENT_TYPE, { id: string; defaultMessage: string }> = {
    [EVENT_TYPE.LPR_CAMERA_DETECTION]: eventMessages.lprCameraDetection,
    [EVENT_TYPE.ACCESS_KEY_READER_DETECTION]: eventMessages.accessKeyReaderDetection,
    [EVENT_TYPE.IDENTIFIER_CREATED]: eventMessages.identifierCreated,
    [EVENT_TYPE.VISITOR_IDENTIFIED]: eventMessages.visitorIdentifier,
    [EVENT_TYPE.GATEWAY_PASS_ALLOWED]: eventMessages.gatewayPassAllowed,
    [EVENT_TYPE.GATEWAY_PASS_NOT_ALLOWED]: eventMessages.gatewayPassNotAllowed,
    [EVENT_TYPE.ACTIVE_STAY_CREATED]: eventMessages.activeStayCreated,
    [EVENT_TYPE.ACTIVE_STAY_PERIOD_CREATED]: eventMessages.activeStayPeriodCreated,
    [EVENT_TYPE.ACTIVE_STAY_PERIOD_COMPLETED]: eventMessages.activeStayPeriodCompleted,
    [EVENT_TYPE.ACTIVE_STAY_COMPLETED]: eventMessages.activeStayCompleted,
    [EVENT_TYPE.ACTIVE_STAY_PAID]: eventMessages.activeStayPaid,
    [EVENT_TYPE.GATEWAY_OPENED]: eventMessages.gatewayOpened,
    [EVENT_TYPE.GATEWAY_CLOSED]: eventMessages.gatewayClosed,
    [EVENT_TYPE.SEND_GATEWAY_OPEN_COMMAND]: eventMessages.sendGatewayOpenCommand,
    [EVENT_TYPE.SEND_GATEWAY_CLOSE_COMMAND]: eventMessages.sendGatewayCloseCommand,
    [EVENT_TYPE.LPR_CAMERA_DETECTION_LOW_CONFIDENCE]: eventMessages.lprCameraDetectionLowConfidence,
};

export const SYSTEM_COMPONENT_TYPE_TRANSLATIONS_MAP = {
    [SYSTEM_COMPONENT_TYPE.SOFTWARE]: systemsMessages.software,
    [SYSTEM_COMPONENT_TYPE.HARDWARE_DEVICE]: systemsMessages.hardware,
};

export const COMPONENT_DATA_TYPE_TRANSLATIONS_MAP = {
    [CAMERA_TYPE.SP_LPR]: systemsMessages.spLPR,
    [CAMERA_TYPE.ICE_LPR]: systemsMessages.iceLPR,
    [CAMERA_TYPE.IPNC_LPR]: systemsMessages.ipncLPR,
    [CAMERA_TYPE.ISAPI1_LPR]: systemsMessages.isapi1LPR,
    [CAMERA_TYPE.EINAR_LPR]: systemsMessages.einarLPR,
    [COMMUNICATOR_TYPE.SP_MQTT]: systemsMessages.spMQTT,
    [FISCAL_DEVICE_TYPE.BG_PAPER]: systemsMessages.bgPaper,
    [FISCAL_DEVICE_TYPE.BG_FUVAS]: systemsMessages.bgFuvas,
};

export const FISCAL_DEVICE_TRANSLATIONS_MAP = {
    [FISCAL_DEVICE_TYPE.BG_PAPER]: systemsMessages.bgPaper,
    [FISCAL_DEVICE_TYPE.BG_FUVAS]: systemsMessages.bgFuvas,
};

export const GATEWAY_DISPLAY_MODEL_TRANSLATIONS_MAP = {
    [GATEWAY_DISPLAY_MODEL.SP]: systemsMessages.spDisplayModal,
    [GATEWAY_DISPLAY_MODEL.HUIDU]: systemsMessages.huiduDisplayModal,
    [GATEWAY_DISPLAY_MODEL.NED200]: systemsMessages.ned200DisplayModal,
    [GATEWAY_DISPLAY_MODEL.NED200C]: systemsMessages.ned200cDisplayModal,
};

export const LISTEN_STATE_TRANSLATIONS_MAP = {
    [LISTEN_STATE.OPEN]: systemsMessages.open,
    [LISTEN_STATE.CLOSED]: systemsMessages.closed,
};

export const POSITION_TRANSLATIONS_MAP = {
    [POSITION.FRONT]: systemsMessages.front,
    [POSITION.CENTER]: systemsMessages.center,
    [POSITION.BACK]: systemsMessages.backPosition,
};

export const IDLE_STATE_TRANSLATIONS_MAP = {
    [IDLE_STATE.DATE]: systemsMessages.idleStateDate,
    [IDLE_STATE.SPACES]: systemsMessages.idleStateSpaces,
    [IDLE_STATE.LPR]: systemsMessages.idleStateLprDetection,
    [IDLE_STATE.TICKET]: systemsMessages.idleStateTicketDetection,
    [IDLE_STATE.CARD]: systemsMessages.idleStateCardDetection,
};

export const ORIENTATION_TRANSLATIONS_MAP = {
    [ORIENTATION.PORTRAIT]: systemsMessages.portrait,
    [ORIENTATION.LANDSCAPE]: systemsMessages.landscape,
};

export const SYSTEM_COMPONENT_TRANSLATIONS_MAP = {
    [SYSTEM_COMPONENT.LPR_CAMERA]: systemsMessages.LPRCamera,
    [SYSTEM_COMPONENT.ACCESS_KEY_READER_COMMUNICATOR]: systemsMessages.keyReaderCommunicator,
    [SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR]: systemsMessages.gatewayCommunicator,
    [SYSTEM_COMPONENT.SENSOR_READER_COMMUNICATOR]: systemsMessages.sensorReaderCommunicator,
    [SYSTEM_COMPONENT.CASH_API]: systemsMessages.cashApi,
    [SYSTEM_COMPONENT.CARD_API]: systemsMessages.cardApi,
    [SYSTEM_COMPONENT.GATEWAY_DISPLAY]: systemsMessages.gatewayDisplay,
    [SYSTEM_COMPONENT.FISCAL_DEVICE]: systemsMessages.fiscalDevice,
    [SYSTEM_COMPONENT.TICKET_PRINTER]: systemsMessages.ticketPrinter,
    [SYSTEM_COMPONENT.BACKEND_HTTP_SERVICE]: systemsMessages.backendHttpService,
    [SYSTEM_COMPONENT.BACKEND_WORKER_SERVICE]: systemsMessages.backendWorkerService,
    [SYSTEM_COMPONENT.BACKEND_INFRA_SERVICE]: systemsMessages.backendInfraService,
};

export const ComponentTypeBadgeOptions: Record<SYSTEM_COMPONENT_TYPE, BadgeOption> = {
    [SYSTEM_COMPONENT_TYPE.SOFTWARE]: {
        label: systemsMessages.software,
        icon: ICON_MAP.CLOUD,
        theme: 'green',
    },
    [SYSTEM_COMPONENT_TYPE.HARDWARE_DEVICE]: {
        label: systemsMessages.hardware,
        icon: ICON_MAP.HARDWARE,
        theme: 'blue',
    },
};

export const ComponentBadgeOptions: Record<SYSTEM_COMPONENT, BadgeOption> = {
    [SYSTEM_COMPONENT.LPR_CAMERA]: {
        label: systemsMessages.LPRCamera,
        icon: ICON_MAP.CCTV_CAMERA,
        theme: 'yellow',
    },
    [SYSTEM_COMPONENT.GATEWAY_COMMUNICATOR]: {
        label: systemsMessages.gatewayCommunicator,
        icon: ICON_MAP.ROAD_BARRIER,
        theme: 'indigo',
    },
    [SYSTEM_COMPONENT.SENSOR_READER_COMMUNICATOR]: {
        label: systemsMessages.sensorReaderCommunicator,
        icon: ICON_MAP.SENSOR,
        theme: 'lime',
    },
    [SYSTEM_COMPONENT.ACCESS_KEY_READER_COMMUNICATOR]: {
        label: systemsMessages.keyReaderCommunicator,
        icon: ICON_MAP.SUBSCRIPTION,
        theme: 'pink',
    },
    [SYSTEM_COMPONENT.CASH_API]: {
        label: systemsMessages.cashApi,
        icon: ICON_MAP.CASH,
        theme: 'green',
    },
    [SYSTEM_COMPONENT.CARD_API]: {
        label: systemsMessages.cardApi,
        icon: ICON_MAP.CARD,
        theme: 'blue',
    },
    [SYSTEM_COMPONENT.GATEWAY_DISPLAY]: {
        label: systemsMessages.gatewayDisplay,
        icon: ICON_MAP.ROAD_BARRIER,
        theme: 'blue',
    },
    [SYSTEM_COMPONENT.FISCAL_DEVICE]: {
        label: systemsMessages.fiscalDevice,
        icon: ICON_MAP.RECEIPT,
        theme: 'gray',
    },
    [SYSTEM_COMPONENT.BACKEND_HTTP_SERVICE]: {
        label: systemsMessages.backendHttpService,
        icon: ICON_MAP.BACKEND_HTTP_SERVICE,
        theme: 'sky',
    },
    [SYSTEM_COMPONENT.BACKEND_WORKER_SERVICE]: {
        label: systemsMessages.backendWorkerService,
        icon: ICON_MAP.BACKEND_WORKER_SERVICE,
        theme: 'violet',
    },
    [SYSTEM_COMPONENT.BACKEND_INFRA_SERVICE]: {
        label: systemsMessages.backendInfraService,
        icon: ICON_MAP.BACKEND_INFRA_SERVICE,
        theme: 'slate',
    },
    [SYSTEM_COMPONENT.TICKET_PRINTER]: {
        label: systemsMessages.ticketPrinter,
        icon: ICON_MAP.PRINTER,
        theme: 'sky',
    },
};

export const ComponentStatusBadgeOptions: Record<SYSTEM_COMPONENT_STATUS, BadgeOption> = {
    [SYSTEM_COMPONENT_STATUS.OPERATIONAL]: {
        label: systemsMessages.operational,
        theme: 'green',
    },
    [SYSTEM_COMPONENT_STATUS.ERROR]: {
        label: systemsMessages.error,
        theme: 'red',
    },
    [SYSTEM_COMPONENT_STATUS.WARNING]: {
        label: systemsMessages.warning,
        theme: 'yellow',
    },
    [SYSTEM_COMPONENT_STATUS.NO_CONNECTION]: {
        label: systemsMessages.noConnection,
        theme: 'yellow',
    },
};

export const ALIGN_TRANSLATION_MAP = {
    [ALIGN.CENTER]: systemsMessages.center,
    [ALIGN.LEFT]: systemsMessages.left,
    [ALIGN.RIGHT]: systemsMessages.right,
};

export const TICKET_PRINTER_TYPE_TRANSLATION_MAP = {
    [TICKET_PRINTER_ROW_TYPE.TEXT]: systemsMessages.text,
    [TICKET_PRINTER_ROW_TYPE.BARCODE]: systemsMessages.barcode,
    [TICKET_PRINTER_ROW_TYPE.LOGO]: systemsMessages.logo,
};

export const COMPONENT_PANEL = 'component-panel';

export const BACKEND_SERVICE_COMPONENTS: SYSTEM_COMPONENT[] = [
    SYSTEM_COMPONENT.BACKEND_HTTP_SERVICE,
    SYSTEM_COMPONENT.BACKEND_WORKER_SERVICE,
    SYSTEM_COMPONENT.BACKEND_INFRA_SERVICE,
];

export const isBackendServiceComponent = (component: SYSTEM_COMPONENT | undefined): boolean =>
    !!component && BACKEND_SERVICE_COMPONENTS.includes(component);
